import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Checkbox, FormControlLabel, MenuItem, TextField, Alert,
} from "@mui/material";
import useAuthStore from "../../../../store/useAuthStore";

const BulkEditItems = ({ open, onClose, selectedIds, onSuccess }) => {
  const { token } = useAuthStore();
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Each field has its own enabled toggle and value
  const [fields, setFields] = useState({
    category: { enabled: false, value: "" },
    minThreshold: { enabled: false, value: "" },
    maxThreshold: { enabled: false, value: "" },
    reorderPoint: { enabled: false, value: "" },
    unitCost: { enabled: false, value: "" },
    isActive: { enabled: false, value: true },
  });

  useEffect(() => {
    if (!open) return;
    setError(""); setSaving(false);
    setFields({
      category: { enabled: false, value: "" },
      minThreshold: { enabled: false, value: "" },
      maxThreshold: { enabled: false, value: "" },
      reorderPoint: { enabled: false, value: "" },
      unitCost: { enabled: false, value: "" },
      isActive: { enabled: false, value: true },
    });
    fetch("/api/getAllInventoryCategories", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setCategories(d.categories || []))
      .catch(console.error);
  }, [open, token]);

  const toggle = (key) => setFields((f) => ({ ...f, [key]: { ...f[key], enabled: !f[key].enabled } }));
  const setVal = (key, value) => setFields((f) => ({ ...f, [key]: { ...f[key], value } }));

  const handleSave = async () => {
    const updates = {};
    Object.entries(fields).forEach(([k, v]) => {
      if (!v.enabled) return;
      if (k === "isActive") updates[k] = !!v.value;
      else if (k === "category") updates[k] = v.value || null;
      else {
        const num = parseFloat(v.value);
        if (!isNaN(num)) updates[k] = num;
      }
    });

    if (Object.keys(updates).length === 0) { setError("Toggle at least one field to update"); return; }

    setSaving(true); setError("");
    try {
      const res = await fetch("/api/bulkUpdateInventoryItems", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selectedIds, updates }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Update failed"); setSaving(false); return; }
      onSuccess?.(data);
      onClose();
    } catch (err) {
      setError("Update failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const numField = (key, label) => (
    <div className="flex items-center gap-3 py-1">
      <Checkbox checked={fields[key].enabled} onChange={() => toggle(key)} />
      <TextField
        label={label}
        type="number"
        size="small"
        disabled={!fields[key].enabled}
        value={fields[key].value}
        onChange={(e) => setVal(key, e.target.value)}
        sx={{ flex: 1 }}
      />
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Edit {selectedIds.length} Item(s)</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" className="mb-3">{error}</Alert>}
        <p className="text-sm text-gray-500 mb-3">Toggle the fields you want to change. Untoggled fields stay as-is.</p>

        <div className="flex items-center gap-3 py-1">
          <Checkbox checked={fields.category.enabled} onChange={() => toggle("category")} />
          <TextField
            select label="Category" size="small"
            disabled={!fields.category.enabled}
            value={fields.category.value}
            onChange={(e) => setVal("category", e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="">(clear category)</MenuItem>
            {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </TextField>
        </div>

        {numField("minThreshold", "Min Threshold")}
        {numField("maxThreshold", "Max Threshold")}
        {numField("reorderPoint", "Reorder Point")}
        {numField("unitCost", "Unit Cost (Nu)")}

        <div className="flex items-center gap-3 py-1">
          <Checkbox checked={fields.isActive.enabled} onChange={() => toggle("isActive")} />
          <FormControlLabel
            disabled={!fields.isActive.enabled}
            control={
              <Checkbox
                checked={fields.isActive.value}
                onChange={(e) => setVal("isActive", e.target.checked)}
              />
            }
            label="Active"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Apply Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditItems;
